import { AuthLayout } from "@/components/layout/AuthLayout";
import { Layout } from "@/components/layout/Layout";
import { SearchIndex } from "@/pages/SearchIndex";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { App as AntApp, ConfigProvider } from "antd";
import enUS from "antd/locale/en_US";
import zhCN from "antd/locale/zh_CN";
import dayjs from "dayjs";
import "dayjs/locale/zh-cn";
import { useEffect } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
// import { SearchIndexDebug } from '@/pages/SearchIndex/debug' // 移除调试页面导入
import ErrorBoundary from "@/components/common/ErrorBoundary";
import { useUserProfile } from "@/hooks/useUserProfile";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ResetPassword from "@/pages/ResetPassword";
import { SearchDetail } from "@/pages/SearchDetail";
import { useTranslation } from "react-i18next";
import "./styles/globals.less";

// 创建QueryClient实例
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5分钟
      gcTime: 1000 * 60 * 30, // 30分钟
      retry: 2,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

// 主题配置
// 使用渐变色 linear-gradient(135deg, rgb(33, 196, 184) 0%, rgb(42, 165, 168) 100%)
// 主要颜色：rgb(33, 196, 184) = #21C4B8
const theme = {
  token: {
    colorPrimary: "#21C4B8", // 渐变色的主要颜色
    colorSuccess: "#21C4B8",
    colorWarning: "#f59e0b",
    colorError: "#ef4444",
    borderRadius: 6,
    fontFamily: "Inter, system-ui, sans-serif",
  },
  components: {
    Layout: {
      headerBg: "#ffffff",
      siderBg: "#f8fafc",
    },
    Menu: {
      itemBg: "transparent",
      itemSelectedBg: "#f0f9ff",
      itemSelectedColor: "#21C4B8",
    },
    Button: {
      primaryColor: "#21C4B8",
      defaultHoverBg: "#f0f9ff",
      defaultHoverBorderColor: "#21C4B8",
      defaultHoverColor: "#21C4B8",
      borderRadius: 20,
      fontWeight: 500,
    },
  },
};

// AppContent组件 - 使用useQuery的组件
function AppContent() {
  const user_id = localStorage.getItem("user_id");
  const { i18n, t } = useTranslation();

  // 使用自定义Hook获取用户profile信息（暂时不使用，但保留以便后续使用）
  useUserProfile(user_id || undefined);

  // 根据语言动态切换 dayjs locale、title 和 meta description
  useEffect(() => {
    const isZh = i18n.language.startsWith("zh");
    // 更新 dayjs locale
    dayjs.locale(isZh ? "zh-cn" : "en");
    // 更新 title
    document.title = t("layout.meta_title");
    // 更新 meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", t("layout.meta_description"));
    }
  }, [i18n.language, t]);

  // 根据语言选择 Ant Design locale
  const antdLocale = i18n.language.startsWith("zh") ? zhCN : enUS;

  return (
    <ConfigProvider locale={antdLocale} theme={theme}>
      <AntApp>
        <Router
          // basename={'/geno'}
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Routes>
            {/* 登录、注册、忘记密码页面使用 AuthLayout */}
            <Route
              path="/auth/login"
              element={
                <AuthLayout>
                  <Login />
                </AuthLayout>
              }
            />
            <Route
              path="/auth/register"
              element={
                <AuthLayout>
                  <Register />
                </AuthLayout>
              }
            />
            <Route
              path="/auth/reset-password"
              element={
                <AuthLayout>
                  <ResetPassword />
                </AuthLayout>
              }
            />
            {/* 其他页面使用 Layout */}
            <Route
              path="/*"
              element={
                <Layout>
                  <Routes>
                    <Route path="/" element={<SearchIndex />} />
                    {/* <Route path="/debug" element={<SearchIndexDebug />} />*/}{" "}
                    {/* 移除调试路由 */}
                    {/* Removed deprecated pages: Dashboard, BrandMonitor, MarketAnalysis */}
                    {/* Reports page temporarily removed */}
                    <Route path="/SearchDetail" element={<SearchDetail />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Layout>
              }
            />
          </Routes>
        </Router>
        <ReactQueryDevtools initialIsOpen={false} />
      </AntApp>
    </ConfigProvider>
  );
}

// 外层App组件 - 设置QueryClientProvider
function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AppContent />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
